import { CheckCircle2, CircleAlert, XCircle } from 'lucide-react';

import {
  NEUTRAL_PH,
  PKW,
  formatLog,
  formatSci,
  pFrom,
  type AcidBaseCheck,
} from './acidBaseCalculations';
import { WEAK_SPECIES } from './acidBaseCalculationsData';

const STATUS_STYLES: Record<AcidBaseCheck['status'], string> = {
  correct: 'border-brand-mint/45 bg-wash-mint',
  misconception: 'border-brand-coral/45 bg-wash-coral',
  incorrect: 'border-brand-coral/45 bg-wash-coral',
  invalid: 'border-brand-sunshine/50 bg-wash-sunshine',
};

export function AcidBaseFeedback({ check, working }: { check: AcidBaseCheck; working: string[] }) {
  return (
    <div role="status" className={`rounded-[1.5rem] border p-4 ${STATUS_STYLES[check.status]}`}>
      <p className="flex items-center gap-2 font-extrabold text-ink">
        {check.status === 'correct' ? (
          <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
        ) : check.status === 'invalid' ? (
          <CircleAlert className="h-5 w-5 text-brand-coral" aria-hidden="true" />
        ) : (
          <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
        )}
        {check.title}
      </p>
      <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{check.feedback}</p>
      {check.status !== 'invalid' && working.length > 0 ? (
        <ol
          className="mt-3 list-decimal space-y-1 rounded-2xl bg-white/80 py-3 pl-8 pr-4 text-sm font-bold text-ink"
          aria-label="Model working"
        >
          {working.map((line) => (
            <li key={line} className="break-words">
              {line}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

const SCALE_TICKS = Array.from({ length: PKW + 1 }, (_, value) => value);
const toPercent = (pH: number) => `${(Math.min(Math.max(pH, 0), PKW) / PKW) * 100}%`;

function phRegion(pH: number): string {
  if (Math.abs(pH - NEUTRAL_PH) < 0.005) return 'neutral';
  return pH < NEUTRAL_PH ? 'acidic' : 'basic';
}

/** pH 0–14 strip at 25 °C with the model value and, when relevant, the learner's value. */
export function PhStrip({ modelPH, learnerPH }: { modelPH: number; learnerPH: number | null }) {
  const label = `pH scale at 25 °C. Model pH ${formatLog(modelPH)} (${phRegion(modelPH)})${
    learnerPH !== null && Number.isFinite(learnerPH) ? `. Your pH ${formatLog(learnerPH)}` : ''
  }.`;
  return (
    <figure className="min-w-0" role="img" aria-label={label}>
      <div className="relative mt-8 h-6 rounded-full bg-gradient-to-r from-brand-coral via-brand-mint to-brand-sky">
        <div
          className="absolute -top-8 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink px-2 py-0.5 text-xs font-extrabold text-white"
          style={{ left: toPercent(modelPH) }}
        >
          ▼ Model {formatLog(modelPH)}
        </div>
        {learnerPH !== null && Number.isFinite(learnerPH) ? (
          <div
            className="absolute top-7 -translate-x-1/2 whitespace-nowrap rounded-full border-2 border-dashed border-ink bg-white px-2 py-0.5 text-xs font-extrabold text-ink"
            style={{ left: toPercent(learnerPH) }}
          >
            ▲ You {formatLog(learnerPH)}
          </div>
        ) : null}
      </div>
      <div className="relative mt-1 h-4 text-[0.65rem] font-bold text-slate2" aria-hidden="true">
        {SCALE_TICKS.map((tick) => (
          <span key={tick} className="absolute -translate-x-1/2" style={{ left: toPercent(tick) }}>
            {tick}
          </span>
        ))}
      </div>
      <figcaption
        className={`flex justify-between text-xs font-extrabold uppercase tracking-wide text-slate2 ${learnerPH !== null ? 'mt-7' : 'mt-2'}`}
      >
        <span>← acidic</span>
        <span>neutral 7</span>
        <span>basic →</span>
      </figcaption>
    </figure>
  );
}

/** Log-scale bars: each step of one pH unit is a tenfold change in ion concentration. */
export function IonBars({ modelPH }: { modelPH: number }) {
  const hydronium = 10 ** -modelPH;
  const hydroxide = 10 ** -(PKW - modelPH);
  const rows = [
    {
      label: '[H₃O⁺]',
      value: hydronium,
      width: ((PKW - modelPH) / PKW) * 100,
      tone: 'bg-brand-coral',
    },
    { label: '[OH⁻]', value: hydroxide, width: (modelPH / PKW) * 100, tone: 'bg-brand-sky' },
  ];
  return (
    <div
      role="img"
      aria-label={`Ion concentrations on a log scale: [H₃O⁺] ${formatSci(hydronium)} and [OH⁻] ${formatSci(hydroxide)} mol per litre. Their product is 1.0 × 10⁻¹⁴.`}
      className="space-y-2"
    >
      {rows.map((row) => (
        <div key={row.label} className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-2">
          <span className="text-sm font-extrabold text-ink">{row.label}</span>
          <div className="h-5 rounded-full bg-surface">
            <div
              className={`flex h-5 items-center justify-end rounded-full pr-2 text-[0.65rem] font-extrabold text-ink ${row.tone}`}
              style={{ width: `${Math.max(row.width, 4)}%` }}
            />
          </div>
          <span className="col-start-2 text-xs font-bold text-slate2">
            {formatSci(row.value)} mol L⁻¹
          </span>
        </div>
      ))}
      <p className="text-xs font-medium text-slate2">
        Log scale: one pH unit = a tenfold change. [H₃O⁺] × [OH⁻] = 1.0 × 10⁻¹⁴ at 25 °C.
      </p>
    </div>
  );
}

const GRID_CELLS = 100;

/** 100 model molecules of a weak acid or base; ionised ones carry a charge sign as well as a tone. */
export function IonisationGrid({ percent, formula }: { percent: number; formula: string }) {
  const ionised = Math.min(GRID_CELLS, Math.max(percent > 0 ? 1 : 0, Math.round(percent)));
  return (
    <figure
      role="img"
      aria-label={`Of every 100 ${formula} particles, about ${formatSci(percent, 2)} % are ionised at equilibrium. The grid shows ${ionised} of 100 cells ionised.`}
    >
      <div className="grid max-w-xs grid-cols-10 gap-1" aria-hidden="true">
        {Array.from({ length: GRID_CELLS }, (_, index) => (
          <span
            key={index}
            className={`grid aspect-square place-items-center rounded text-[0.6rem] font-extrabold ${
              index < ionised ? 'bg-brand-coral text-ink' : 'bg-wash-sky text-slate2'
            }`}
          >
            {index < ionised ? '±' : ''}
          </span>
        ))}
      </div>
      <figcaption className="mt-2 text-xs font-medium text-slate2">
        ± = ionised (ion pair formed). Plain = still a molecule. {formatSci(percent, 2)} % ionised;
        each cell is 1 %, rounded.
      </figcaption>
    </figure>
  );
}

const ACIDS_BY_STRENGTH = WEAK_SPECIES.filter((species) => species.type === 'acid').sort(
  (left, right) => right.k - left.k,
);
const LADDER_MAX_PKA = 10;

/** Reviewed weak acids ordered by pKa; the bar length shows pKa, a stronger acid has a shorter bar. */
export function StrengthLadder({ highlightId }: { highlightId: string | null }) {
  return (
    <ol className="space-y-1.5" aria-label="Weak acids ordered from stronger to weaker (25 °C)">
      {ACIDS_BY_STRENGTH.map((species) => {
        const pKa = pFrom(species.k);
        const active = species.id === highlightId;
        return (
          <li
            key={species.id}
            className={`grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_3rem] items-center gap-2 rounded-xl px-2 py-1 text-xs ${
              active ? 'bg-wash-sunshine font-extrabold text-ink' : 'font-bold text-slate2'
            }`}
          >
            <span className="truncate">
              {species.formula}
              {active ? ' ◀' : ''}
            </span>
            <span className="h-2.5 rounded-full bg-surface">
              <span
                className={`block h-2.5 rounded-full ${active ? 'bg-ink' : 'bg-brand-sky'}`}
                style={{ width: `${(pKa / LADDER_MAX_PKA) * 100}%` }}
              />
            </span>
            <span className="text-right tabular-nums">pKa {formatLog(pKa)}</span>
          </li>
        );
      })}
    </ol>
  );
}
